/**
 * Role Display Name Mapping
 *
 * Maps internal role strings to user-facing display names.
 * Internal roles remain unchanged for workflow/permissions logic.
 *
 * Vendor roles:  maker → Initiator, checker → Approver
 * Auditor roles: auditor_maker → Accountant, auditor_checker → Reviewer
 * Admin roles:   tenant_admin → Controller, super_admin → Administrator
 */

// Display names keyed by department type for context-aware display
const ROLE_DISPLAY_NAMES_BY_DEPT: Record<string, Record<string, string>> = {
  vendor: {
    maker: 'Initiator',
    checker: 'Approver',
    tenant_admin: 'Controller',
  },
  auditor: {
    auditor_maker: 'Accountant',
    auditor_checker: 'Reviewer',
    tenant_admin: 'Controller',
    super_admin: 'Administrator',
  },
};

// Flat display names (when department type is unknown)
const ROLE_DISPLAY_NAMES: Record<string, string> = {
  maker: 'Initiator',
  checker: 'Approver',
  tenant_admin: 'Controller',
  auditor_maker: 'Accountant',
  auditor_checker: 'Reviewer',
  super_admin: 'Administrator',
};

// Workflow stage display names (replacing old vendor/auditor stage labels)
export const STAGE_DISPLAY_NAMES: Record<string, { label: string; shortLabel: string }> = {
  uploaded: { label: 'Document Uploaded', shortLabel: 'Uploaded' },
  extracted: { label: 'Data Extracted', shortLabel: 'Extracted' },
  vendor_maker: { label: 'Initiator Review', shortLabel: 'Initiator' },
  vendor_checker: { label: 'Approver Review', shortLabel: 'Approver' },
  auditor_maker: { label: 'Accountant Review', shortLabel: 'Accountant' },
  auditor_checker: { label: 'Reviewer Final Approval', shortLabel: 'Reviewer' },
};

/**
 * Get display name for a role, optionally considering department type.
 * Falls back to titlecased role string if no mapping exists.
 */
export function getRoleDisplayName(role: string, deptType?: string): string {
  if (deptType && ROLE_DISPLAY_NAMES_BY_DEPT[deptType]?.[role]) {
    return ROLE_DISPLAY_NAMES_BY_DEPT[deptType][role];
  }
  if (ROLE_DISPLAY_NAMES[role]) {
    return ROLE_DISPLAY_NAMES[role];
  }
  // Fallback: title-case the role
  return role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Full option labels for the User create / edit role selectors, in the
 * "{Group} - {Role}" form requested by the business. Keyed by internal role
 * name. Custom roles fall back to their department group + display name.
 */
export const ROLE_OPTION_LABELS: Record<string, string> = {
  maker: 'Processing - Initiator',
  checker: 'Processing - Approver',
  supervisor: 'Processing - Supervisor',
  auditor_maker: 'Reviewer - Accountant / Auditor Maker',
  auditor_checker: 'Reviewer - Accountant / Auditor Checker',
  tenant_admin: 'Controller - Tenant Admin',
  super_admin: 'Administrator - Super Admin',
};

export function getRoleOptionLabel(role: string, deptKind?: string | null): string {
  if (ROLE_OPTION_LABELS[role]) return ROLE_OPTION_LABELS[role];
  const group = deptKind === 'vendor' ? 'Processing' : deptKind === 'auditor' ? 'Reviewer' : '';
  const base = getRoleDisplayName(role);
  return group ? `${group} - ${base}` : base;
}

// Display order for the role selectors: Processing (Initiator, Approver,
// Supervisor / custom) → Review (Accountant, Reviewer / custom) → Controller
// → Administrator → anything else.
const ROLE_SEQUENCE: Record<string, number> = {
  maker: 0, checker: 1,
  auditor_maker: 20, auditor_checker: 21,
  tenant_admin: 40, super_admin: 41,
};

export function getRoleSortKey(role: string, deptKind?: string | null): number {
  if (role in ROLE_SEQUENCE) return ROLE_SEQUENCE[role];
  if (deptKind === 'vendor') return 10;   // custom Processing roles (e.g. Supervisor) after Initiator/Approver
  if (deptKind === 'auditor') return 30;  // custom Review roles after Accountant/Reviewer
  return 50;
}

/**
 * Get role options for dropdowns based on department type.
 * Returns array of { value, label, description } for select elements.
 */
export function getRoleOptions(deptType?: string): { value: string; label: string; description: string }[] {
  if (deptType === 'auditor') {
    return [
      { value: 'auditor_maker', label: 'Accountant', description: 'Review documents (first level)' },
      { value: 'auditor_checker', label: 'Reviewer', description: 'Approve documents (final level)' },
    ];
  }
  // Default: vendor roles
  return [
    { value: 'maker', label: 'Initiator', description: 'Upload and submit documents' },
    { value: 'checker', label: 'Approver', description: 'Review and approve documents' },
  ];
}

/**
 * Get role icon emoji based on role.
 */
export function getRoleIcon(role: string): string {
  const icons: Record<string, string> = {
    super_admin: '🛡️',
    tenant_admin: '👑',
    checker: '✅',
    maker: '📝',
    auditor_maker: '📋',
    auditor_checker: '🔍',
  };
  return icons[role] || '👤';
}

/**
 * Get user-facing display label for a document status.
 * Internal status strings remain unchanged — only the display text changes.
 */
export function getStatusDisplayLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Pending Processing',
    in_review: 'Processing',
    extracted: 'Draft',
    extraction_failed: 'Extraction Failed',
    resubmitted: 'Resubmitted',

    // Vendor statuses
    vendor_pending: 'Pending Approver',
    vendor_review: 'Pending Approver',
    vendor_approved: 'Approved by Approver',
    vendor_rejected: 'Rejected by Approver',

    // Auditor statuses
    auditor_pending: 'Pending Accountant',
    auditor_review: 'Pending Reviewer',
    auditor_approved: 'Fully Approved',
    auditor_rejected: 'Rejected by Accountant',

    // Final statuses
    approved: 'Fully Approved',
    rejected: 'Rejected',
    processed: 'Processed',
    completed: 'Completed',

    // Archive statuses — approved stays "Fully Approved" until archival job picks up
    archiving: 'Archiving',
    archived: 'Archived',
    archive_failed: 'Archive Failed',
  };
  return labels[status] || status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Build the "This document is pending <role> <action>." sentence shown on the
 * read-only extracted-fields panel. The role word comes from getRoleDisplayName
 * so the message, the status badge (getStatusDisplayLabel) and the stepper
 * (STAGE_DISPLAY_NAMES) all speak the same Approver / Accountant / Reviewer
 * vocabulary — never the old "Vendor Checker" / "Auditor Maker" strings.
 */
export function getPendingApprovalMessage(status: string): string {
  const next: Record<string, { role: string; action: string }> = {
    vendor_review:    { role: 'checker',         action: 'approval' },
    vendor_approved:  { role: 'auditor_maker',   action: 'review' },
    auditor_pending:  { role: 'auditor_maker',   action: 'review' },
    auditor_review:   { role: 'auditor_checker', action: 'approval' },
  };
  const n = next[status];
  if (!n) return 'This document is pending next level approval.';
  return `This document is pending ${getRoleDisplayName(n.role)} ${n.action}.`;
}

export default ROLE_DISPLAY_NAMES;
